// OT module API client.
//
// One transport layer for every /app/api/ot/ endpoint, following the convention
// the newer IPD api modules use: REACT_APP_BACKEND_ROUTE_URL plus the shared
// sessionStorage auth headers.
//
// Two response shapes, and the difference matters:
//
//   GET  -> `res.data.data` is the payload. `get()` unwraps it.
//   POST/PATCH/DELETE -> the whole envelope is returned, because a mutation's
//        `message` and any `warnings` are part of what the caller must show.
//
// **Business failures arrive as HTTP 200 with status "Failed" and
// is_valid: true.** They are not exceptions and must not be thrown: the backend
// keeps is_valid true on purpose, because a 2xx body carrying is_valid:false
// trips the global axios interceptor's multi-machine-login guard and throws the
// user to the login screen (src/pages/login/Axios-interceptor.js). So a refused
// booking or an illegal lifecycle move comes back through `ok === false` with a
// message to render, never a rejected promise.
//
// Only transport and network failures reject.

import axios from "axios";

const BASE =
  process.env.REACT_APP_BACKEND_ROUTE_URL ||
  "https://manage-dev.hlthtek.com/app/api/";

function authHeaders(extra = {}) {
  const token = sessionStorage.getItem("accessToken") || "";
  return {
    Authorization: `Bearer ${token}`,
    "APP-MODE": sessionStorage.getItem("APP_MODE") || "",
    "ORG-CODE": sessionStorage.getItem("ORG_CODE") || "",
    "USER-ACCESS-KEY": sessionStorage.getItem("USER_ACCESS_KEY") || "",
    USERNAME: sessionStorage.getItem("userName") || "",
    ...extra,
  };
}

function url(path, params) {
  const query = params
    ? Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null && v !== "")
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
        .join("&")
    : "";
  return `${BASE}ot/${path}${query ? `?${query}` : ""}`;
}

/** Normalise any OT response into { ok, message, data, ...extras }. */
function envelope(body) {
  const payload = body || {};
  return {
    ...payload,
    ok: payload.status === "Success",
    message: payload.message || "",
    data: payload.data,
  };
}

async function get(path, params) {
  const res = await axios.get(url(path, params), { headers: authHeaders() });
  const body = envelope(res?.data);
  if (!body.ok) {
    // A failed GET still carries a message worth showing rather than an empty
    // screen with no explanation.
    const err = new Error(body.message || "Could not load");
    err.body = body;
    throw err;
  }
  return body.data;
}

/** Like get(), but returns the whole envelope — for reads whose count or flags
 *  the caller needs alongside the payload. */
async function getFull(path, params) {
  const res = await axios.get(url(path, params), { headers: authHeaders() });
  return envelope(res?.data);
}

async function send(method, path, body) {
  const res = await axios({
    method,
    url: url(path),
    data: body,
    headers: authHeaders({ "Content-Type": "application/json" }),
  });
  return envelope(res?.data);
}

const post = (path, body) => send("post", path, body);
const patch = (path, body) => send("patch", path, body);
const del = (path, body) => send("delete", path, body);

// ── module ────────────────────────────────────────────────────────────────

export const ping = () => get("ping/");
export const fetchDashboard = () => get("dashboard/");

// ── setup & masters ───────────────────────────────────────────────────────

export const fetchSetup = () => get("setup/");

export const fetchRooms = (params) => get("operating_rooms/", params);
export const createRoom = (body) => post("operating_rooms/", body);
export const updateRoom = (id, body) => patch(`operating_rooms/${id}/`, body);
export const deactivateRoom = (id) => del(`operating_rooms/${id}/`);

// The three simple vocabularies share one shape, so they share one set of
// helpers keyed by their path rather than three near-identical triples.
export const VOCAB_PATHS = {
  anaesthesia_types: "anaesthesia_types",
  surgery_positions: "surgery_positions",
  incision_types: "incision_types",
};
export const fetchVocab = (kind, params) => get(`${VOCAB_PATHS[kind]}/`, params);
export const createVocab = (kind, body) => post(`${VOCAB_PATHS[kind]}/`, body);
export const updateVocab = (kind, id, body) => patch(`${VOCAB_PATHS[kind]}/${id}/`, body);
export const deactivateVocab = (kind, id) => del(`${VOCAB_PATHS[kind]}/${id}/`);

export const fetchAttendeeTypes = (params) => get("attendee_types/", params);
// Real doctors and nurses at this facility, so a case role is filled by a
// person with an id rather than a typed name. `member_type` narrows the list
// to whichever the role expects.
export const searchOtStaff = (params) => get("staff/search/", params);
export const createAttendeeType = (body) => post("attendee_types/", body);
export const updateAttendeeType = (id, body) => patch(`attendee_types/${id}/`, body);
export const deactivateAttendeeType = (id) => del(`attendee_types/${id}/`);

export const fetchRoomBlocks = (params) => get("room_blocks/", params);
export const createRoomBlock = (body) => post("room_blocks/", body);
export const cancelRoomBlock = (id) => del(`room_blocks/${id}/`);

// ── the doctor's request ──────────────────────────────────────────────────
//
// The inbound half: a doctor asks for an operation, OT decides where and
// when. `mine=1` is the doctor's own list and needs no OT permission — it is
// how they see what became of what they asked for.

// The theatre diary as a DOCTOR may see it: read only, and other surgeons'
// patients are redacted by the server, not by the screen.
export const fetchDoctorSchedule = (date) => get("doctor_schedule/", { date });

export const fetchOtRequests = (params) => get("requests/", params);
export const raiseOtRequest = (body) => post("requests/", body);
export const withdrawOtRequest = (id, reason) =>
  patch(`requests/${id}/`, { withdrawn_reason: reason });
// `bump` is optional and, when given, must also say where the displaced
// patient goes — the server refuses a bump that would leave them unbooked.
export const scheduleOtRequest = (id, body) => post(`requests/${id}/schedule/`, body);

// ── cases ─────────────────────────────────────────────────────────────────

export const searchPatients = (q) => get("patients/search/", { q });
export const fetchCases = (params) => getFull("cases/", params);
export const createCase = (body) => post("cases/", body);
export const fetchCase = (id) => get(`cases/${id}/`);
export const updateCase = (id, body) => patch(`cases/${id}/`, body);

export const fetchProcedures = (caseId) => get(`cases/${caseId}/procedures/`);
export const addProcedure = (caseId, body) => post(`cases/${caseId}/procedures/`, body);
export const updateProcedure = (caseId, id, body) =>
  patch(`cases/${caseId}/procedures/${id}/`, body);
export const removeProcedure = (caseId, id) => del(`cases/${caseId}/procedures/${id}/`);

export const fetchLifecycle = (caseId) => get(`cases/${caseId}/lifecycle/`);
// `extra` carries the cancellation category and the avoidability judgement.
// The server refuses a cancellation without a category and serves the list of
// valid ones from fetchLifecycle, so the form never offers one it will reject.
export const moveLifecycle = (caseId, to, reason, extra) =>
  post(`cases/${caseId}/lifecycle/`, { to, reason, ...(extra || {}) });

// ── bookings & schedule ───────────────────────────────────────────────────

export const fetchCaseBookings = (caseId) => get(`cases/${caseId}/bookings/`);
export const bookTheatre = (caseId, body) => post(`cases/${caseId}/bookings/`, body);
export const rescheduleBooking = (id, body) => patch(`bookings/${id}/`, body);
export const cancelBooking = (id, reason) => del(`bookings/${id}/`, { reason });
export const fetchSchedule = (params) => get("schedule/", params);

// ── consent ───────────────────────────────────────────────────────────────

export const fetchConsentTemplate = () => get("consent_template/");
export const fetchConsents = (caseId) => get(`cases/${caseId}/consents/`);
export const recordConsent = (caseId, body) => post(`cases/${caseId}/consents/`, body);
export const updateConsent = (id, body) => patch(`consents/${id}/`, body);
export const openConsentFile = (id) => get(`consents/${id}/file/`);

// ── phase 2: pre-op ───────────────────────────────────────────────────────

export const fetchPac = (caseId) => get(`cases/${caseId}/pac/`);
export const recordPac = (caseId, body) => post(`cases/${caseId}/pac/`, body);
export const approveCase = (caseId, body) => post(`cases/${caseId}/approval/`, body || {});

export const fetchTeam = (caseId) => get(`cases/${caseId}/team/`);
export const addTeamMember = (caseId, body) => post(`cases/${caseId}/team/`, body);
export const updateTeamMember = (id, body) => patch(`team/${id}/`, body);
export const removeTeamMember = (id) => del(`team/${id}/`);

export const authoriseFinalList = (caseId) => post(`cases/${caseId}/final_list/`, {});
export const fetchFinalList = (params) => get("final_list/", params);

export const fetchPreopChecklist = (caseId) => get(`cases/${caseId}/preop_checklist/`);
export const savePreopChecklist = (caseId, body) =>
  post(`cases/${caseId}/preop_checklist/`, body);
export const fetchWhoChecklist = (caseId) => get(`cases/${caseId}/who_checklist/`);
export const saveWhoChecklist = (caseId, body) => post(`cases/${caseId}/who_checklist/`, body);

// ── phase 2: patient flow ─────────────────────────────────────────────────

export const fetchCallSlips = (caseId) => get(`cases/${caseId}/call_slip/`);
export const issueCallSlip = (caseId, body) => post(`cases/${caseId}/call_slip/`, body || {});
export const updatePorterTask = (id, body) => patch(`call_slips/${id}/`, body);
export const wardCheckout = (caseId, body) => post(`cases/${caseId}/ward_checkout/`, body || {});
export const fetchHoldingCheckin = (caseId) => get(`cases/${caseId}/holding_checkin/`);
export const holdingCheckin = (caseId, body) =>
  post(`cases/${caseId}/holding_checkin/`, body || {});
export const fetchVitals = (caseId, params) => get(`cases/${caseId}/vitals/`, params);
export const recordVitals = (caseId, body) => post(`cases/${caseId}/vitals/`, body);
export const moveTracking = (caseId, to) => post(`cases/${caseId}/tracking/`, { to });
export const fetchBoard = (params) => get("board/", params);

// ── phase 3: intra-op ─────────────────────────────────────────────────────

export const fetchIdentityCheck = (caseId) => get(`cases/${caseId}/identity_check/`);
export const saveIdentityCheck = (caseId, body) => post(`cases/${caseId}/identity_check/`, body);
export const enterTheatre = (caseId, body) => post(`cases/${caseId}/in_or/`, body || {});

export const fetchAnaesthesiaRecord = (caseId) => get(`cases/${caseId}/anaesthesia_record/`);
export const saveAnaesthesiaRecord = (caseId, body) =>
  post(`cases/${caseId}/anaesthesia_record/`, body);
export const addAnaesthesiaDrug = (caseId, body) =>
  post(`cases/${caseId}/anaesthesia_drugs/`, body);
export const addAnaesthesiaMonitor = (caseId, body) =>
  post(`cases/${caseId}/anaesthesia_monitor/`, body);

export const fetchOrders = (caseId, params) => get(`cases/${caseId}/orders/`, params);
export const placeOrder = (caseId, body) => post(`cases/${caseId}/orders/`, body);
export const retryOrder = (id) => post(`orders/${id}/retry/`, {});

/**
 * Search the inventory item master.
 *
 * Goes to the EMR's existing inventory proxy rather than a second one of our
 * own — inventory is a separate microservice and there is no reason for OT to
 * learn how to reach it. That is also why this is the one call in this file
 * that does not sit under `ot/`.
 *
 * Price field names vary across the catalogue (mrp, sale_price, unit_rate),
 * so the caller picks whichever is present rather than this pretending to
 * know. Anything unrecognised leaves the price blank and typable.
 */
export async function searchInventoryItems(query) {
  const res = await axios.get(
    `${BASE}inventory/items?search=${encodeURIComponent(query)}&page_size=15`,
    { headers: authHeaders() }
  );
  const body = res?.data;
  return body?.results || body?.data?.results || body?.data || [];
}

/** The first price the catalogue actually carries for an item. */
export function itemPriceOf(item) {
  const keys = ["sale_price", "mrp", "unit_rate", "unit_price", "sale_rate", "rate"];
  const key = keys.find((k) => item?.[k] !== undefined && item?.[k] !== null && item?.[k] !== "");
  return key ? String(item[key]) : "";
}

export const fetchConsumption = (caseId) => get(`cases/${caseId}/consumption/`);
export const recordConsumption = (caseId, body) => post(`cases/${caseId}/consumption/`, body);
export const retryConsumptionCharge = (id) => post(`consumption/${id}/post_charge/`, {});

export const fetchSwabCounts = (caseId) => get(`cases/${caseId}/swab_count/`);
export const startSwabCount = (caseId, body) => post(`cases/${caseId}/swab_count/`, body);
export const updateSwabCount = (id, body) => patch(`swab_counts/${id}/`, body);
export const verifySwabCount = (id, body) => post(`swab_counts/${id}/verify/`, body);
// One AORN counting juncture. `counted` is how many were ADDED at the ADDED
// juncture and the total on the field at every other one — see the backend's
// record_count_event for why those are genuinely different questions.
export const recordCountJuncture = (id, body) =>
  post(`swab_counts/${id}/juncture/`, body);

// The recall query: which patients received a given device. Needs a device
// identifier or a batch number — it will not list every implant ever used.
export const lookupImplants = (params) => get("implants/", params);

export const fetchReferrals = (caseId) => get(`cases/${caseId}/referral/`);
export const raiseReferral = (caseId, body) => post(`cases/${caseId}/referral/`, body);
export const updateReferral = (id, body) => patch(`referrals/${id}/`, body);

export const fetchNote = (caseId, kind) => get(`cases/${caseId}/notes/${kind}/`);
export const saveNote = (caseId, kind, body) => post(`cases/${caseId}/notes/${kind}/`, body);
export const fetchMergedNotes = (caseId) => get(`cases/${caseId}/notes/merged/`);

// Named default text for a note. Free-standing and applied by hand — nothing
// auto-fills, because a template that fills itself is a template that gets
// signed without being read.
export const fetchNoteTemplates = (params) => get("note_templates/", params);
export const createNoteTemplate = (body) => post("note_templates/", body);
export const retireNoteTemplate = (id) => del(`note_templates/${id}/`);

// The operation, read from OUTSIDE OT.
//
// Certified notes only — a draft appearing in another clinician's view of the
// record, or in a discharge summary, reads as final when it is not.
//
// Two scopes because there are two readers: the doctor console asks what
// operations this PATIENT has had, the discharge summary asks what happened
// during this ADMISSION.
export const fetchPatientOperationNotes = (patientId) =>
  get(`patients/${encodeURIComponent(patientId)}/operation_notes/`);
export const fetchAdmissionOperationNotes = (admissionId) =>
  get(`admissions/${admissionId}/operation_notes/`);

// Audit-safe reversal. Nothing is deleted: a posted charge gets a linked
// CANCELLATION that backs the amount out of the account, and one still pending
// is ignored instead.
// The service master, searched. Uses `search_medical_services` rather than
// `get_medical_services` because this one filters by service_type too, which
// is what a lab or imaging order needs — those are not Operation Theatre
// services and cannot be found by an OT classification.
//
// Classification is passed by NAME. The numeric id differs per tenant schema.
export async function searchMedicalServices({
  name = "",
  serviceType,
  classificationName,
  limit = 25,
} = {}) {
  const params = { name, limit };
  if (serviceType) params.service_type = serviceType;
  if (classificationName) params.classification_name = classificationName;
  // Not an /ot/ route: this one lives on the main EMR surface, so the OT
  // path prefix `url()` adds would be wrong.
  const query = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  try {
    const res = await axios.get(`${BASE}search_medical_services/?${query}`, {
      headers: authHeaders(),
    });
    const rows = res?.data?.data;
    return Array.isArray(rows) ? rows : [];
  } catch (err) {
    // A picker that cannot reach the master must not break the form it sits in.
    return [];
  }
}

// A whole charge sheet in one action, so the bill is not six separate forms.
// Each line succeeds or fails on its own.
export const raiseCharges = (caseId, body) => post(`cases/${caseId}/billing/`, body);

export const reverseCaseCharge = (chargeId, reason) =>
  post(`case_charges/${chargeId}/reverse/`, { reason });

// The whole case at once, for an operation that was abandoned or billed in
// error. Each charge succeeds or fails on its own.
export const reverseCaseCharges = (caseId, body) =>
  post(`cases/${caseId}/billing/reverse/`, body);

export const otCheckout = (caseId, body) => post(`cases/${caseId}/ot_checkout/`, body);

// Correcting the theatre clock the OT_ROOM charge is computed from. The
// change is recorded with who made it and what the times were before.
export const correctWheelTimes = (caseId, body) =>
  patch(`cases/${caseId}/wheel_times/`, body);

// ── phase 4: recovery, billing, reports ───────────────────────────────────

export const fetchRecovery = (caseId) => get(`cases/${caseId}/recovery/`);
export const receiveInRecovery = (caseId, body) => post(`cases/${caseId}/recovery/`, body || {});
export const recordAldrete = (caseId, body) => post(`cases/${caseId}/recovery/score/`, body);
export const fetchRecoveryOrders = (caseId) => get(`cases/${caseId}/recovery_orders/`);
export const addRecoveryOrder = (caseId, body) => post(`cases/${caseId}/recovery_orders/`, body);
export const markReadyForTransfer = (caseId) => post(`cases/${caseId}/recovery_ready/`, {});
export const recoveryCheckout = (caseId, body) =>
  post(`cases/${caseId}/recovery_checkout/`, body);

export const fetchBillingParameters = (params) => get("billing_parameters/", params);
export const createBillingParameter = (body) => post("billing_parameters/", body);
export const updateBillingParameter = (id, body) => patch(`billing_parameters/${id}/`, body);
export const deactivateBillingParameter = (id) => del(`billing_parameters/${id}/`);

export const fetchCaseBilling = (caseId) => get(`cases/${caseId}/billing/`);
export const raiseCaseCharge = (caseId, body) => post(`cases/${caseId}/billing/`, body);
export const applyConcession = (id, body) => patch(`case_charges/${id}/`, body);
export const retryCaseCharge = (id) => post(`case_charges/${id}/retry/`, {});

export const fetchReport = (report, params) => get(`reports/${report}/`, params);

export const fetchMaintenanceLogs = (params) => get("maintenance_logs/", params);
export const recordMaintenance = (body) => post("maintenance_logs/", body);

// The NABH environment register. The GET doubles as the compliance view: it
// returns `coverage`, the days each theatre was NOT read on — the finding at an
// assessment is almost never a bad reading, it is a missing one.
export const fetchEnvironmentLogs = (params) => get("environment_logs/", params);
export const recordEnvironmentReading = (body) => post("environment_logs/", body);
